import { PageHeader } from '../../components/common/PageHeader';
import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/joy';
import {
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
  Bank as AccountBalance,
  Receipt,
  CurrencyDollar as AttachMoney,
  ChartBar as Assessment,
  Plus as Add,
  ArrowsClockwise as Refresh,
  Gauge as Speed,
  Calendar as CalendarMonth,
  ChartLine as ShowChart,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { apiAccountingClient } from '../../lib/apiAccounting';
import type { AccountingDashboardData, JournalEntry } from '../../types/accounting';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';

// Memoized formatting functions for performance
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const DashboardComponent: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<AccountingDashboardData | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      const data = await apiAccountingClient.getDashboardData();
      setDashboardData(data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchDashboardData();
    const refreshOnFocus = () => { void fetchDashboardData(); };
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [fetchDashboardData]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    void fetchDashboardData();
    const refreshOnFocus = () => { void fetchDashboardData(); };
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" sx={{ mb: 2 }}>
        {error}<Button variant="soft" color="danger" onClick={handleRefresh} loading={refreshing}>Retry</Button>
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert color="warning">No data available</Alert>
    );
  }

  const source = dashboardData.data_status;
  const billing = dashboardData.billing_activity;
  const ledgerAvailable = !source || source.has_accounts;
  const ledgerCurrency = (amount: number) => ledgerAvailable ? formatCurrency(amount) : '—';
  const netIncome = dashboardData.total_revenue_mtd - dashboardData.total_expenses_mtd;
  const isProfit = netIncome >= 0;

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5, mb: 3 }}>
        <PageHeader title="Financial overview" description="Review your balances and month-to-date results, then open an entry to see its details." />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& > button': { flex: { xs: '1 1 calc(50% - 4px)', md: '0 0 auto' } } }}>
          <Button
            variant="soft"
            startDecorator={<Speed />}
            onClick={() => navigate('/accounting/analytics')}
          >
            Analytics
          </Button>
          <Button
            startDecorator={<Add />}
            onClick={() => navigate('/accounting/journal-entries/new')}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New&nbsp;</Box>Journal&nbsp;Entry
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Refresh />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {billing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Box>
                <Typography level="h4">Live Billing Activity</Typography>
                <Typography level="body-sm">From your invoices and recorded payments. These totals are separate from your accounting ledger.</Typography>
              </Box>
              <Button variant="outlined" onClick={() => navigate('/operations/billing')}>Open Billing</Button>
            </Box>
            <Grid container spacing={2}>
              {[
                { label: 'Outstanding Invoices', value: billing.outstanding_invoices, detail: `${billing.outstanding_count} unpaid invoices` },
                { label: 'Invoiced This Month', value: billing.invoiced_mtd, detail: 'Issued invoices, including tax' },
                { label: 'Collected This Month', value: billing.collected_mtd, detail: 'Completed payments, less recorded refunds' },
              ].map(item => (
                <Grid key={item.label} xs={12} sm={4}>
                  <Typography level="body-sm">{item.label}</Typography>
                  <Typography level="h3">{formatCurrency(item.value)}</Typography>
                  <Typography level="body-xs">{item.detail}</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
      {source && (
        <Box sx={{ mb: 2 }}>
          <Typography level="h4">Accounting Ledger</Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            Posted entries and opening balances through {source.as_of_date} · {source.time_zone}
          </Typography>
          {source.book_of_record === 'quickbooks' ? (
            <Alert color="neutral">QuickBooks is your book of record. The balances below are ApexOS ledger entries, not live QuickBooks balances. Review your financial statements in QuickBooks.</Alert>
          ) : !source.has_accounts ? (
            <Alert color="warning" endDecorator={<Button variant="soft" color="warning" onClick={() => navigate('/accounting/settings')}>Accounting Setup</Button>}>
              Your chart of accounts has not been set up. Ledger balances are unavailable; live billing activity is shown above. Existing invoices need accounting review before they can be included in your books.
            </Alert>
          ) : source.posted_entry_count === 0 ? (
            <Alert color="warning">No posted journal entries yet. Review opening balances and invoice postings before relying on financial reports.</Alert>
          ) : source.unposted_invoice_count > 0 ? (
            <Alert color="warning">{source.unposted_invoice_count} issued invoices have no posted invoice entry. Ledger totals may be incomplete; review these invoices with your accounting team.</Alert>
          ) : null}
          <Typography level="body-xs" sx={{ mt: 1 }}>Updated {new Date(source.refreshed_at).toLocaleTimeString()} · Refreshes when you return to this page</Typography>
        </Box>
      )}

      {/* Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Assets Card */}
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Assets
                  </Typography>
                  <Typography level="h3">
                    {ledgerCurrency(dashboardData.total_assets)}
                  </Typography>
                </Box>
                <AccountBalance color="var(--joy-palette-primary-500)" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Liabilities Card */}
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Total Liabilities
                  </Typography>
                  <Typography level="h3">
                    {ledgerCurrency(dashboardData.total_liabilities)}
                  </Typography>
                </Box>
                <Receipt color="var(--joy-palette-warning-500)" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Card */}
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Revenue this month
                  </Typography>
                  <Typography level="h3">
                    {ledgerCurrency(dashboardData.total_revenue_mtd)}
                  </Typography>
                </Box>
                <TrendingUp color="var(--joy-palette-success-500)" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Net Income Card */}
        <Grid xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Box>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Net income this month
                  </Typography>
                  <Typography level="h3" sx={{ color: isProfit ? 'success.main' : 'danger.main' }}>
                    {ledgerCurrency(netIncome)}
                  </Typography>
                </Box>
                {isProfit ? (
                  <TrendingUp color="var(--joy-palette-success-500)" />
                ) : (
                  <TrendingDown color="var(--joy-palette-danger-500)" />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Secondary Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                Cash Balance
              </Typography>
              <Typography level="h4">
                {ledgerCurrency(dashboardData.cash_balance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                Accounts Receivable
              </Typography>
              <Typography level="h4">
                {ledgerCurrency(dashboardData.accounts_receivable)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                Accounts Payable
              </Typography>
              <Typography level="h4">
                {ledgerCurrency(dashboardData.accounts_payable)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Journal Entries */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="h4">Recent Journal Entries</Typography>
          <Button
            variant="plain"
            size="sm"
            onClick={() => navigate('/accounting/journal-entries')}
          >
            View All
          </Button>
        </Box>
        <ResponsiveTable<JournalEntry>
          rows={dashboardData.recent_entries}
          getRowKey={(e) => e.id}
          rowActions={(e) => (
            <Button size="sm" variant="plain" onClick={() => navigate(`/accounting/journal-entries/${e.id}`)}>
              View
            </Button>
          )}
          emptyState={<Typography level="body-sm" sx={{ textAlign: 'center', p: 2 }}>No journal entries found</Typography>}
          columns={[
            { key: 'entry_number', sortValue: (row) => row.entry_number, header: 'Entry #', mobileVariant: 'header', render: (e) => <Typography level="title-sm">{e.entry_number}</Typography> },
            { key: 'description', sortValue: (row) => row.description ?? row.memo, header: 'Description', mobileVariant: 'body', render: (e) => <Typography level="body-sm">{e.description || e.memo || '-'}</Typography> },
            { key: 'date', sortValue: (row) => row.entry_date ? new Date(row.entry_date) : null, header: 'Date', render: (e) => formatDate(e.entry_date) },
            { key: 'type', sortValue: (row) => row.entry_type, header: 'Type', render: (e) => <Chip size="sm" variant="soft" color="neutral">{e.entry_type}</Chip> },
            { key: 'status', sortValue: (row) => row.status, header: 'Status', render: (e) => <Chip size="sm" variant="soft" color={e.status === 'POSTED' ? 'success' : e.status === 'DRAFT' ? 'warning' : 'danger'}>{e.status}</Chip> },
            { key: 'amount', sortValue: (row) => Number(row.total_debits ?? 0), header: 'Amount', render: (e) => <Typography level="body-sm" sx={{ fontWeight: 'md' }}>{formatCurrency(e.total_debits || 0)}</Typography> },
          ]}
        />
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2 }}>Quick Actions</Typography>
          <Grid container spacing={2}>
            <Grid xs={6} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<AccountBalance />}
                onClick={() => navigate('/accounting/chart-of-accounts')}
              >
                Manage Accounts
              </Button>
            </Grid>
            <Grid xs={6} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<Receipt />}
                onClick={() => navigate('/accounting/journal-entries')}
              >
                View Entries
              </Button>
            </Grid>
            <Grid xs={6} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<Assessment />}
                onClick={() => navigate('/accounting/reports/trial-balance')}
              >
                Trial Balance
              </Button>
            </Grid>
            <Grid xs={6} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<AttachMoney />}
                onClick={() => navigate('/accounting/reports/profit-loss')}
              >
                P&L Report
              </Button>
            </Grid>
            <Grid xs={6} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<CalendarMonth />}
                onClick={() => navigate('/accounting/periods')}
              >
                Period Management
              </Button>
            </Grid>
            <Grid xs={6} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startDecorator={<ShowChart />}
                onClick={() => navigate('/accounting/analytics')}
              >
                Analytics
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

// Export memoized component to prevent unnecessary re-renders
export const Dashboard = memo(DashboardComponent);
